import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from "@angular/material/dialog";
import {
  ADD_CREDIT_CARD_DIALOG,
  CREDIT_CARD_DETAILS_REQUIRED_MESSAGE,
  CREDIT_CARDS_DIALOG,
  REQUEST_PAYMENT_DIALOG
} from "../../constants/global.constants";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {UserService} from "../../service/user.service";
import {CreditCardModel} from "../../interface/creditCardModel";
import {InternalTransferService} from "../../service/internalTransfer.service";
import {UserResponse} from "../../interface/userResponse";
import {MockupDataModel} from "../../qr-copy-message/mockup-data.model";
import {SnackBarUtil} from "../../qr-copy-message/snack-bar-util.service";
import {SharedConstants} from "../../utilityClass/sharedConstants";
import {BankCardService} from "../../service/bankCard.service";
import {BalanceFormatterUtil} from '../balance-formatter-util ';
import { ActivityTabs } from '../../enums/activity-tabs';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-activity-dialogs',
  templateUrl: './activity-dialogs.component.html',
  styleUrl: './activity-dialogs.component.scss',
})
export class ActivityDialogsComponent implements OnInit {
  isSelected: boolean = false;
  paymentForm!: FormGroup;
  creditCardForm!: FormGroup;
  showAccountNumber: boolean = false;
  creditCards: CreditCardModel[] = []
  creditCard!: CreditCardModel
  oldCreditCard : CreditCardModel | null = null
  currentUser!: UserResponse
  currency = SharedConstants.CURRENCY
  selectedCreditCardId: number | null = null
  hasAmountError: boolean | undefined;
  accountNumberPattern = /^\d{16}$/;
  errorMessage: string | undefined;
  isFromBankAccount = false

  protected readonly ADD_CREDIT_CARD_DIALOG = ADD_CREDIT_CARD_DIALOG;
  protected readonly CREDIT_CARDS_DIALOG = CREDIT_CARDS_DIALOG;
  protected readonly REQUEST_PAYMENT_DIALOG = REQUEST_PAYMENT_DIALOG;
  protected readonly BalanceFormatterUtil = BalanceFormatterUtil;

  constructor(private dialogRef: MatDialogRef<ActivityDialogsComponent>,
              private translate: TranslateService,
              @Inject(MAT_DIALOG_DATA) private dialogData: {
                message: string,
                amount: number,
                balance: number,
                teamId: number | null,
                isFromBankAccount: boolean,
                activeTab: string
              },
              private dialog: MatDialog,
              private fb: FormBuilder,
              private userService: UserService,
              private internalTransferService: InternalTransferService,
              private snackBarUtil: SnackBarUtil,
              private bankCardService: BankCardService,
              private translateService: TranslateService) {
  }

  ngOnInit(): void {
    this.isFromBankAccount = this.data.isFromBankAccount
    this.userService.currentUser.subscribe(user => {
      this.currentUser = user
      this.userService.findByEmail(this.currentUser.email).subscribe(user => {
        this.currentUser = user
        if (this.currentUser.bankCards?.length) {
          this.creditCards = this.currentUser.bankCards
          const selectedCard = this.creditCards.find(card => card.isSelected);
          this.selectedCreditCardId = selectedCard ? selectedCard.id : (this.creditCards.length > 0 ? this.creditCards[0].id : null);
        }
        this.paymentForm = this.fb.group({
          amount: ['', Validators.required],
          creditCardId: [this.selectedCreditCardId ? this.selectedCreditCardId : '', Validators.required]
        });

        if (this.data.activeTab == ActivityTabs.MY_TEAM) {
          this.paymentForm.get("amount")?.setValue(BalanceFormatterUtil.getFormattedBalance(this.data.balance))
          this.paymentForm.get("amount")?.disable();
        }
      })
    });
    this.paymentForm = this.fb.group({
      amount: ['', Validators.required],
      creditCardId: [this.creditCards && this.creditCards.length > 0 ? this.creditCards[0].id : '', Validators.required]
    });
    this.creditCardForm = this.fb.group({
      accountNumber: ['', [Validators.required, Validators.pattern(this.accountNumberPattern)]],
      cardholderName: ['', Validators.required]
    });
  }

  get data(): {
    message: string
    amount: number,
    balance: number,
    teamId: number | null,
    isFromBankAccount: boolean,
    activeTab: string
  } {
    return this.dialogData;
  }

  get accountNumberInputType(): string {
    const accountNumber = this.creditCardForm.get('accountNumber')?.value;
    return accountNumber && !this.showAccountNumber ? 'password' : 'text';

  }

  onClose(): void {
    this.dialogRef.close(this.oldCreditCard ? undefined : this.creditCard);
  }

  openAddCreditCardDialog() {
    const dialogRef = this.dialog.open(ActivityDialogsComponent, {
      width: '430px',
      data: {
        message: ADD_CREDIT_CARD_DIALOG,
        amount: this.paymentForm.get("amount")?.value,
        balance: this.data.balance,
        teamId: this.data.teamId,
        isFromBankAccount: this.isFromBankAccount
      }
    });
    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this.creditCard = res
        this.creditCards.forEach(card => card.isSelected = false);
        this.creditCards.unshift(res)
        this.paymentForm.get("creditCardId")?.setValue(res.id);
      }
    });
  }

  saveCreditCard() {
    if (this.creditCardForm.valid) {
      this.bankCardService.saveCreditCard(this.mapToCreditCardModel()).subscribe(res => {
        this.creditCard = res
        this.dialogRef.close(res);
      })
    }
  }

  changeCreditCard(creditCard: CreditCardModel) {
    this.creditCard = creditCard
    this.oldCreditCard = this.creditCard
  }

  toggleAccountNumberVisibility() {
    this.showAccountNumber = !this.showAccountNumber;
  }

  getLastFourDigits(accountNumber: string): string {
    if (accountNumber && accountNumber.length > 4) {
      return accountNumber.slice(-4);
    }
    return "";
  }

  saveRequest() {
    if (this.isBalanceAvailable(this.data.amount) && this.creditCardForm.valid) {
      this.saveRequestPayment(this.data.amount, this.mapToCreditCardModel())
    }
  }

  onSubmit() {
    if (!this.isBalanceAvailable(this.paymentForm.get('amount')?.value)) return;
    this.hasAmountError = this.paymentForm.get('amount')?.invalid;
    if (this.paymentForm.valid) {
      this.saveRequestPayment(this.paymentForm.get('amount')?.value, null)
    } else {
      this.showSnackbar(
        MockupDataModel.error(this.translateService.instant('credit_card_details_required_message')),
        SnackBarUtil.CLASSES.error
      );

    }
  }

  deleteCard(id: number) {
    this.bankCardService.deleteBankCard(this.currentUser.id, id).subscribe(res => {
      this.creditCards = this.creditCards.filter(card => card.id !== res.id);
    })
  }

  editCreditCard(creditCard: CreditCardModel | undefined) {
    if (creditCard) {
      this.creditCard = creditCard
      this.bankCardService.editCreditCard(this.currentUser.id, creditCard.id).subscribe(_ => {
        this.oldCreditCard = null
        this.creditCards = this.creditCards.map(updatedCreditCard => {
          return {
            ...updatedCreditCard,
            isSelected: updatedCreditCard.id === creditCard.id
          };
        });
      })
    }
    if (this.data.isFromBankAccount) {
      this.dialogRef.close(this.creditCard);
    }
  }

  cancel() {
    this.dialogRef.close(this.creditCards[0]);
  }

  onAmountInput(event: Event): void {
    if (this.data.activeTab == ActivityTabs.MY_TEAM){
      this.paymentForm.get('amount')?.setValue(BalanceFormatterUtil.getFormattedBalance(this.data.balance))
    }else {
      const input = event.target as HTMLInputElement;
      let value = input.value.replace(/[^0-9.]/g, '');
      if (value.includes('-')) {
        value = value.replace('-', '');
      }
      if (value.startsWith('0') && value.length > 1 && !value.startsWith('0.')) {
        value = value.replace(/^0+/, '');
      }
      const [integerPart, decimalPart] = value.split('.');
      if (decimalPart?.length > 2) {
        value = `${integerPart}.${decimalPart.substring(0, 2)}`;
      }
      input.value = value;
      const control = this.paymentForm.get('amount');
      if (control) {
        const numericValue = value ? parseFloat(value) : null;
        control.setValue(numericValue, {emitEvent: false});
        if (!value) {
          this.errorMessage = this.translateService.instant('enter_amount');
          this.hasAmountError = true;
        } else if (numericValue === null || numericValue <= 0) {
          this.errorMessage = 'Enter a valid amount';
          this.hasAmountError = true;
        } else {
          this.hasAmountError = false;
        }
      }
    }
  }

  copyCode(creditCard: CreditCardModel): void {
    navigator.clipboard.writeText(creditCard.accountNumber).then(
      () => {
        this.showSnackbar(MockupDataModel.success(this.translateService.instant('activity.card_number_copied')), SnackBarUtil.CLASSES.success);
      }
    );
  }


  onAccountNumberInput(event: any): void {
    const value = event.target.value;
    const numericValue = value.replace(/\D/g, '');

    if (numericValue.length <= 16) {
      event.target.value = numericValue;
    } else {
      event.target.value = numericValue.slice(0, 16);
    }
  }

  private saveRequestPayment(amount: number, bankCardCreateRequest: CreditCardModel | null) {

    const requestTransferRequest = {
      id: null,
      bankCardId: this.paymentForm.get('creditCardId')?.value,
      amount: amount,
      bankCardCreateRequest: bankCardCreateRequest,
      teamId: this.data?.teamId || null
    };
    this.internalTransferService.saveRequest(requestTransferRequest).subscribe({
      next: (res) => {
        if (res.bankCard) {
          this.creditCards.unshift(res.bankCard);
        }
        this.dialogRef.close(this.creditCards[0]);
      },
      error: (error) => {
        if (error.status === 409 && error.error?.message) {
          switch (error.error.message) {
            case 'LIMIT_REACHED':
              this.showSnackbar(
                MockupDataModel.error(this.translate.instant('transfer_limit_reached')),
                SnackBarUtil.CLASSES.error
              );
              break;
            case 'PENDING_REQUEST_EXISTS':
              this.showSnackbar(
                MockupDataModel.error(this.translate.instant('transfer_pending_request')),
                SnackBarUtil.CLASSES.error
              );
              break;
            default:
              this.showSnackbar(
                MockupDataModel.error(error.error.message),
                SnackBarUtil.CLASSES.error
              );
          }
        } else if (error.status === 400 && error.error?.message) {
          this.showSnackbar(
            MockupDataModel.error(this.translate.instant('request_amount_exceeds_balance')),
            SnackBarUtil.CLASSES.error
          );
        }
      }
    });
  }

  private isBalanceAvailable(amount: number | null | undefined): boolean {
    if (!this.data.balance) {
      return false;
    }

    if (amount && amount > this.data.balance) {
      this.showSnackbar(
        MockupDataModel.error(this.translate.instant('request_amount_exceeds_balance')),
        SnackBarUtil.CLASSES.error
      );
      return false;
    }
    return true;
  }

  private mapToCreditCardModel(): CreditCardModel {
    const formValue = this.creditCardForm.value;

    return {
      id: 0,
      accountNumber: formValue.accountNumber,
      cardholderName: formValue.cardholderName,
      isDeleted: false,
      isSelected: true,
    };
  }

  private showSnackbar(data: MockupDataModel, panelClassNames: string[]): void {
    this.snackBarUtil.openSnackBar({
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: panelClassNames,
      data: data
    });
  }

  protected readonly SharedConstants = SharedConstants;
}
